import type { JSX } from 'react';
import { useState } from 'react';
import type { WireUser } from '@logistics/contracts';
import { failureFrom } from '@/lib/result';
import { cn } from '@/lib/utils';
import type { Me } from '../auth/use-me';
import { button, card, monoLabel } from '../shell/look';
import { Modal } from '../shell/modal';
import { PageHead } from '../shell/page-head';
import type { Route } from '../shell/routes';
import { EmptyState, ErrorState, LoadingState } from '../shell/states';
import { navigate } from '../shell/use-route';
import { AllowUserModal } from './allow-user-modal';
import { InvitesPanel } from './invites-panel';
import { SourceAccessModal } from './source-access-modal';
import { useChangeUser, useUsers } from './use-users';
import { USER_COLUMNS, UserRow } from './user-row';

/** Allowed users (design L393–429). `#/users/add` opens "Allow a user". Admin only. */
export function UsersPage({ route, me }: { route: Route; me: Me }): JSX.Element {
  const admin = me.role === 'admin';
  const close = (): void => navigate({ page: 'users' });
  return (
    <section className="animate-rise">
      <PageHead
        eyebrow="ACCESS CONTROL"
        title="Allowed users"
        description="The bot ignores anyone not on this list. Identify people by Telegram username or numeric ID, and by email for the Web UI."
        action={
          admin ? (
            <button
              type="button"
              className={button.primary}
              onClick={() => navigate({ page: 'users', action: 'add' })}
            >
              + Allow user
            </button>
          ) : undefined
        }
      />
      {admin ? (
        <>
          <UserTable />
          <InvitesPanel />
        </>
      ) : (
        <div className={card}>
          <EmptyState
            title="Only admins can manage users"
            body="Ask an admin to allow someone new or to change a role."
          />
        </div>
      )}
      <p className="text-mute mx-0.5 mt-3.5 mb-0 font-mono text-[12.5px]">
        Unknown senders receive no reply. Blocked attempts are logged under Activity.
      </p>
      {admin && route.action === 'add' && <AllowUserModal onClose={close} />}
    </section>
  );
}

function UserTable(): JSX.Element {
  const users = useUsers(true);
  const change = useChangeUser();
  const [removing, setRemoving] = useState<WireUser | undefined>(undefined);
  const [scoping, setScoping] = useState<WireUser | undefined>(undefined);

  if (users.isPending) return <LoadingState rows={5} />;
  if (users.isError) {
    return <ErrorState {...failureFrom(users.error)} onRetry={() => void users.refetch()} />;
  }
  return (
    <div className={cn(card, 'overflow-x-auto')}>
      <div className="min-w-[870px]">
        <div className={cn(USER_COLUMNS, monoLabel, 'border-line border-b py-3')}>
          <span>USER</span>
          <span>TELEGRAM</span>
          <span>EMAIL</span>
          <span>ROLE</span>
          <span>CHANNELS</span>
          <span>SOURCES</span>
          <span className="text-right">ACCESS</span>
        </div>
        {users.data.length === 0 && (
          <div className="text-mute p-10 text-center text-sm">
            No users allowed yet. The bot will stay silent until you add one.
          </div>
        )}
        {users.data.map((user) => (
          <UserRow
            key={user.id}
            user={user}
            busy={change.isPending && change.variables.user.id === user.id}
            onRole={(role) =>
              change.mutate({ user, patch: { role }, done: `${user.name} is now ${role}` })
            }
            onAccess={(enabled) =>
              change.mutate({
                user,
                patch: { enabled },
                done: enabled
                  ? `${user.name} re-enabled`
                  : `${user.name} disabled. The bot will ignore them.`,
              })
            }
            onSources={() => setScoping(user)}
            onRemove={() => setRemoving(user)}
          />
        ))}
      </div>
      {scoping !== undefined && (
        <SourceAccessModal user={scoping} onClose={() => setScoping(undefined)} />
      )}
      {removing !== undefined && (
        <ConfirmRemove
          user={removing}
          onCancel={() => setRemoving(undefined)}
          onConfirm={() => {
            change.mutate({ user: removing, remove: true, done: `${removing.name} removed` });
            setRemoving(undefined);
          }}
        />
      )}
    </div>
  );
}

/** Removing someone is one click in the design; a confirm keeps it from being an accident. */
function ConfirmRemove({
  user,
  onCancel,
  onConfirm,
}: {
  user: WireUser;
  onCancel: () => void;
  onConfirm: () => void;
}): JSX.Element {
  return (
    <Modal eyebrow="ACCESS CONTROL" title={`Remove ${user.name}?`} onClose={onCancel}>
      <div className="flex flex-col gap-4 px-7 py-6">
        <p className="text-mute m-0 text-[13.5px] leading-[1.55]">
          They lose access on every channel at once, and their session ends. You can allow them
          again later; to pause them instead, switch their access off.
        </p>
        <div className="flex justify-end gap-2.5">
          <button type="button" onClick={onCancel} className={button.outline}>
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={cn(button.primary, 'bg-bad text-white')}
          >
            Remove
          </button>
        </div>
      </div>
    </Modal>
  );
}
